import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { X, Camera, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const QRScanner = ({ onScan, onClose }) => {
  const [isStarting, setIsStarting] = useState(true);
  const [cameraFailed, setCameraFailed] = useState(false);
  const scannerRef = useRef(null);
  const fileInputRef = useRef(null);
  const mountedRef = useRef(true);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  useEffect(() => {
    mountedRef.current = true;
    let html5QrCode = null;
    let stopped = false;

    const startScanner = async () => {
      try {
        html5QrCode = new Html5Qrcode('qr-reader-container');
        scannerRef.current = html5QrCode;

        await html5QrCode.start(
          { facingMode: 'environment' },
          { fps: 5, qrbox: { width: 220, height: 220 }, disableFlip: true, videoConstraints: { facingMode: 'environment', width: { ideal: 640 }, height: { ideal: 640 } } },
          (decodedText) => {
            if (mountedRef.current && !stopped) {
              stopped = true;
              html5QrCode.stop().catch(() => {});
              onScanRef.current(decodedText);
            }
          },
          () => {}
        );
        if (mountedRef.current) setIsStarting(false);
      } catch (err) {
        console.error('Camera error:', err);
        if (mountedRef.current) {
          setCameraFailed(true);
          setIsStarting(false);
        }
      }
    };

    const timer = setTimeout(() => {
      if (mountedRef.current) startScanner();
    }, 300);

    return () => {
      mountedRef.current = false;
      clearTimeout(timer);
      if (scannerRef.current) {
        try { scannerRef.current.stop().catch(() => {}); } catch {}
        scannerRef.current = null;
      }
    };
  }, []);

  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const html5QrCode = new Html5Qrcode('qr-file-hidden');
      const result = await html5QrCode.scanFile(file, true);
      onScanRef.current(result);
    } catch {
      // Retry with different config
    }
  }, []);

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" data-testid="qr-scanner-modal">
      <Card className="w-full max-w-md p-5 space-y-4 relative rounded-2xl">
        <Button
          variant="ghost" size="sm"
          onClick={onClose}
          className="absolute top-3 right-3 z-10"
          data-testid="close-scanner"
        >
          <X className="w-5 h-5" />
        </Button>

        <div className="text-center pt-2">
          <h2 className="text-lg font-bold text-slate-900">Scanner le QR code</h2>
          <p className="text-sm text-slate-500 mt-1">Pointez vers le QR code du garage</p>
        </div>

        {/* Live camera area */}
        {!cameraFailed && (
          <div
            id="qr-reader-container"
            className="w-full rounded-xl overflow-hidden bg-slate-900"
            style={{ minHeight: '300px', maxHeight: '300px', position: 'relative' }}
          />
        )}

        <style>{`
          #qr-reader-container video {
            object-fit: cover !important;
            width: 100% !important;
            height: 300px !important;
            border-radius: 12px;
          }
          #qr-reader-container img[alt="Info icon"] { display: none !important; }
          #qr-reader-container > div { overflow: hidden !important; }
        `}</style>

        {/* Hidden div for file-based scanning */}
        <div id="qr-file-hidden" style={{ display: 'none' }} />

        {isStarting && !cameraFailed && (
          <div className="flex flex-col items-center py-4">
            <Camera className="w-8 h-8 text-blue-500 animate-pulse mb-2" />
            <p className="text-sm text-slate-500">Activation de la camera...</p>
          </div>
        )}

        {cameraFailed && (
          <div className="text-center py-6 space-y-3">
            <Camera className="w-12 h-12 text-slate-400 mx-auto" />
            <p className="text-sm text-slate-600">
              La camera n'a pas pu demarrer.<br />
              Prenez une photo du QR code a la place.
            </p>
          </div>
        )}

        {/* File upload - always visible as alternative */}
        <div className="space-y-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileUpload}
            className="hidden"
            data-testid="qr-file-input"
          />
          <Button
            variant={cameraFailed ? 'default' : 'outline'}
            onClick={() => fileInputRef.current?.click()}
            className={`w-full ${cameraFailed ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}`}
            data-testid="take-photo-button"
          >
            <Upload className="mr-2 w-4 h-4" />
            {cameraFailed ? 'Prendre une photo du QR code' : 'Ou prendre une photo'}
          </Button>
        </div>

        <Button
          variant="ghost"
          onClick={onClose}
          className="w-full text-slate-500"
          data-testid="cancel-scan"
        >
          Annuler
        </Button>
      </Card>
    </div>
  );
};

export default QRScanner;
